import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { CardTemplate, CardTemplateField } from '@/hooks/useCardTemplates';
import { getSignatureFontById } from '@/data/signatureFonts';
import { cn } from '@/lib/utils';
import { calculateFitFontSize } from '@/lib/fitText';
import { getMrzText, isMrzFieldKey } from '@/lib/mrzGenerator';
import { resolveCanonicalKey } from '@/lib/linkedFields';
import { LiveQRCode } from '@/components/admin/LiveQRCode';
import { User, Pen } from 'lucide-react';

interface EditableCardPreviewProps {
  template: CardTemplate;
  formData: Record<string, string>;
  photo: string | null;
  signatureText: string;
  signatureFont: string;
  showBack: boolean;
  onFieldChange: (fieldKey: string, value: string) => void;
  onPhotoClick: () => void;
  onSignatureClick: () => void;
  className?: string;
}

export const EditableCardPreview: React.FC<EditableCardPreviewProps> = ({
  template,
  formData,
  photo,
  signatureText,
  signatureFont,
  showBack,
  onFieldChange,
  onPhotoClick,
  onSignatureClick,
  className,
}) => {
  const { t } = useLanguage();
  const [frontLoaded, setFrontLoaded] = useState(false);
  const [backLoaded, setBackLoaded] = useState(false);
  const [containerWidth, setContainerWidth] = useState(500);
  const containerRef = useRef<HTMLDivElement>(null);
  const font = getSignatureFontById(signatureFont);

  // Calculate scale factor based on container width (reference: 500px)
  const scaleFactor = containerWidth / 500;

  // Observe container size for responsive scaling
  useEffect(() => {
    if (!containerRef.current) return;
    
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Filter fields by side from DB
  const frontFields = useMemo(() => {
    return (template.fields || []).filter((field) => field.position_side === 'front');
  }, [template.fields]);

  const backFields = useMemo(() => {
    return (template.fields || []).filter((field) => field.position_side === 'back');
  }, [template.fields]);

  // Photo should show on its designated side
  const showPhotoOnFront = template.photo_side === 'front';

  // Signature button component with fit-to-field font sizing
  const SignatureButton = () => {
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [fontSize, setFontSize] = useState(16);

    useEffect(() => {
      if (!buttonRef.current || !signatureText) return;

      const calculate = () => {
        const containerWidth = buttonRef.current?.getBoundingClientRect().width || 0;
        if (containerWidth <= 0) return;

        const availableWidth = containerWidth * 0.9;
        const optimalSize = calculateFitFontSize(
          signatureText,
          font?.fontFamily || 'cursive',
          availableWidth,
          20, // max
          8   // min
        );
        setFontSize(optimalSize);
      };

      calculate();

      const resizeObserver = new ResizeObserver(calculate);
      resizeObserver.observe(buttonRef.current);
      return () => resizeObserver.disconnect();
    }, [signatureText, font?.fontFamily]);

    return (
      <button
        ref={buttonRef}
        type="button"
        onClick={onSignatureClick}
        className={cn(
          'absolute flex items-center justify-center cursor-pointer',
          'border-2 border-dashed hover:border-primary hover:scale-[1.02]',
          'transition-all duration-200 rounded-sm',
          signatureText 
            ? 'border-transparent bg-transparent' 
            : 'border-gray-500/60 bg-white/50 hover:bg-white/70'
        )}
        style={{
          left: `${template.signature_position.x}%`,
          top: `${template.signature_position.y}%`,
          width: `${template.signature_position.width}%`,
          height: `${template.signature_position.height}%`,
          transform: template.signature_position.rotation 
            ? `rotate(${template.signature_position.rotation}deg)` 
            : undefined,
        }}
      >
        {signatureText ? (
          <span
            className="text-gray-900 whitespace-nowrap truncate w-full text-center"
            style={{
              fontFamily: font?.fontFamily || 'cursive',
              fontSize: `${fontSize}px`,
            }}
          >
            {signatureText}
          </span>
        ) : (
          <div className="flex items-center gap-1">
            <Pen className="w-2.5 h-2.5 text-gray-600" />
            <span className="text-[clamp(5px,1vw,8px)] text-gray-600 font-semibold uppercase tracking-wide">
              Signature
            </span>
          </div>
        )}
      </button>
    );
  };

  // Render a field display (read-only style for this preview)
  const renderField = (field: CardTemplateField) => {
    // Use canonical key for linked field synchronization
    const canonicalKey = resolveCanonicalKey(template.fields || [], field.field_key);
    const effectiveValue = formData[canonicalKey] || '';
    
    const baseFontSize = parseFloat(field.font_size || '12') || 12;
    const scaledFontSize = Math.max(6, baseFontSize * scaleFactor);

    // QR Code fields - use LiveQRCode component
    if (field.field_type === 'qrcode') {
      return (
        <div
          key={field.id}
          className="absolute pointer-events-none"
          style={{
            left: `${field.position_x}%`,
            top: `${field.position_y}%`,
            width: `${field.position_width}%`,
            height: `${field.position_height}%`,
            transform: field.rotation ? `rotate(${field.rotation}deg)` : undefined,
          }}
        >
          <LiveQRCode
            formData={formData}
            templateName={template.name}
            className="w-full h-full"
          />
        </div>
      );
    }

    // MRZ fields - auto-generated, read-only
    if (isMrzFieldKey(field.field_key)) {
      const mrzText = getMrzText(field.field_key, formData, 150);
      
      return (
        <div
          key={field.id}
          className="absolute pointer-events-none"
          style={{
            left: `${field.position_x}%`,
            top: `${field.position_y}%`,
            width: `${field.position_width}%`,
            height: `${field.position_height}%`,
            transform: field.rotation ? `rotate(${field.rotation}deg)` : undefined,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <span
            style={{
              fontFamily: field.font_family || 'Gilroy-Bold',
              fontSize: `${scaledFontSize}px`,
              fontWeight: field.font_weight || 'normal',
              color: field.font_color || '#000000',
              whiteSpace: 'nowrap',
              textTransform: 'uppercase',
              letterSpacing: '-0.02em',
            }}
          >
            {mrzText}
          </span>
        </div>
      );
    }

    // Regular fields
    const isRightAligned = field.text_align === 'right';
    const isCenterAligned = field.text_align === 'center';
    
    return (
      <div
        key={field.id}
        className={cn(
          "absolute flex items-center cursor-pointer hover:bg-white/10 transition-colors rounded",
          isRightAligned && "justify-end",
          isCenterAligned && "justify-center"
        )}
        style={{
          left: `${field.position_x}%`,
          top: `${field.position_y}%`,
          width: `${field.position_width}%`,
          height: `${field.position_height}%`,
          transform: field.rotation ? `rotate(${field.rotation}deg)` : undefined,
          fontFamily: field.font_family || 'Gilroy-Bold',
          fontSize: `${scaledFontSize}px`,
          color: field.font_color || '#000000',
          fontWeight: field.font_weight || 'normal',
          textTransform: (field.text_transform as React.CSSProperties['textTransform']) || 'uppercase',
        }}
        onClick={() => onFieldChange(field.field_key, effectiveValue)}
      >
        <span className="truncate">
          {effectiveValue || field.placeholder || field.label}
        </span>
      </div>
    );
  };

  return (
    <div className={cn('space-y-3', className)}>
      {/* Card Container with 3D Flip Effect */}
      <div ref={containerRef} className="relative w-full aspect-[1.6/1] perspective-1000">
        <div
          className={cn(
            'relative w-full h-full transition-transform duration-700 transform-style-3d',
            showBack && 'rotate-y-180'
          )}
        >
          {/* Front of Card */}
          <div className="absolute inset-0 backface-hidden rounded-xl overflow-hidden shadow-2xl bg-secondary">
            <div className="relative w-full h-full">
              {!frontLoaded && (
                <div className="absolute inset-0 bg-secondary animate-pulse" />
              )}

              <img
                src={template.front_image_url}
                alt={`${template.name} front`}
                className={cn(
                  'w-full h-full object-contain transition-opacity duration-300',
                  frontLoaded ? 'opacity-100' : 'opacity-0'
                )}
                onLoad={() => setFrontLoaded(true)}
              />

              {/* Photo Hotspot - clickable */}
              {showPhotoOnFront && template.photo_position && (
                <button
                  type="button"
                  onClick={onPhotoClick}
                  className={cn(
                    'absolute flex items-center justify-center overflow-hidden cursor-pointer',
                    'border-2 border-dashed hover:border-primary hover:scale-[1.02]',
                    'transition-all duration-200 rounded-sm',
                    photo 
                      ? 'border-transparent' 
                      : 'border-gray-500/60 bg-white/50 hover:bg-white/70'
                  )}
                  style={{
                    left: `${template.photo_position.x}%`,
                    top: `${template.photo_position.y}%`,
                    width: `${template.photo_position.width}%`,
                    height: `${template.photo_position.height}%`,
                  }}
                >
                  {photo ? (
                    <img
                      src={photo}
                      alt="Your photo"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center gap-0.5 p-0.5">
                      <User className="w-1/4 h-1/4 text-gray-600" />
                      <span className="text-[clamp(4px,0.9vw,7px)] text-gray-600 font-semibold uppercase tracking-wide">
                        Foto
                      </span>
                    </div>
                  )}
                </button>
              )}

              {/* Signature Area - only on front if signature_side === 'front' */}
              {template.signature_position && template.signature_side === 'front' && (
                <SignatureButton />
              )}

              {/* Front Side Fields from DB */}
              {frontFields.map(renderField)}
            </div>
          </div>

          {/* Back of Card */}
          <div className="absolute inset-0 backface-hidden rotate-y-180 rounded-xl overflow-hidden shadow-2xl bg-secondary">
            <div className="relative w-full h-full">
              {!backLoaded && (
                <div className="absolute inset-0 bg-secondary animate-pulse" />
              )}
              <img
                src={template.back_image_url || template.front_image_url}
                alt={`${template.name} back`}
                className={cn(
                  'w-full h-full object-contain transition-opacity duration-300',
                  backLoaded ? 'opacity-100' : 'opacity-0'
                )}
                onLoad={() => setBackLoaded(true)}
              />

              {/* Signature Area - only on back if signature_side === 'back' */}
              {template.signature_position && template.signature_side === 'back' && (
                <SignatureButton />
              )}

              {/* Back Side Fields from DB */}
              {backFields.map(renderField)}
            </div>
          </div>
        </div>
      </div>

      {/* Preview Label */}
      <p className="text-xs text-center text-muted-foreground">
        {t('product.previewHintEdit')}
      </p>
    </div>
  );
};

EditableCardPreview.displayName = 'EditableCardPreview';
